import type { AnalysisProps } from "./comparison-observation.js";
import { displaySides } from "./comparison-presentation.js";
import "../../styles/comparison-trend.css";

type TrendRows = NonNullable<AnalysisProps["detail"]["performance"]>["rounds"];
const signed = (v: number) => `${v > 0 ? "+" : ""}${v.toFixed(2)}`;
const date = (v: string) =>
  new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

export function RoundTrend({
  detail,
  onOpenRoundId,
}: AnalysisProps): React.JSX.Element | null {
  const rows =
    detail.performance?.rounds
      .filter(
        (r) =>
          r.date &&
          r.holes > 0 &&
          Number.isFinite(r.left) &&
          Number.isFinite(r.right),
      )
      .slice(-8) ?? [];
  if (rows.length < 2) return null;
  const sides = displaySides(detail.relation);
  const average = (side: "left" | "right") =>
    rows.reduce((sum, r) => sum + r[side], 0) /
    rows.reduce((sum, r) => sum + r.holes, 0);
  return (
    <section className="rc-trend rc-form-trend">
      <h2>Recent form</h2>
      <p className="rc-caption">
        {detail.filters.basis === "net" ? "Net" : "Gross"} strokes over par per
        hole · Lower is better
      </p>
      <div className="rc-form-legend">
        {sides.map((side, i) => (
          <div key={side} data-series={i}>
            <span>
              <i aria-hidden="true" />
              {detail.relation[side].name}
            </span>
            <strong data-leading={average(side) < average(sides[1 - i]!)}>
              {signed(average(side))}
            </strong>
          </div>
        ))}
      </div>
      <p className="rc-form-period">Average · Last {rows.length} rounds</p>
      <TrendPlot rows={rows} sides={sides} />
      <div className="rc-trend-rounds">
        {rows.map((row) => (
          <button
            key={row.roundId}
            disabled={!onOpenRoundId}
            onClick={() => onOpenRoundId?.(row.roundId)}
            aria-label={`${date(row.date!)}: ${sides.map((s) => `${detail.relation[s].name} ${signed(row[s] / row.holes)}`).join(", ")}`}
          >
            <small>{date(row.date!)}</small>
            <span>
              {sides.map((side, i) => (
                <b key={side} data-series={i}>
                  {signed(row[side] / row.holes)}
                </b>
              ))}
            </span>
            <small>{row.holes} holes</small>
          </button>
        ))}
      </div>
    </section>
  );
}

function TrendPlot({
  rows,
  sides,
}: {
  rows: TrendRows;
  sides: ["left" | "right", "left" | "right"];
}): React.JSX.Element {
  const { low, high, x, y, last, ends, labelY } = trendCoordinates(rows, sides);
  return (
    <svg
      viewBox="0 0 320 170"
      role="img"
      aria-label="Strokes over par per hole. Lower line is better. Oldest round on the left, latest on the right."
    >
      {[low, (low + high) / 2, high].map((v) => (
        <g key={v}>
          <line
            className="rc-chart-grid"
            x1="38"
            x2="254"
            y1={y(v)}
            y2={y(v)}
          />
          <text className="rc-form-axis" x="30" y={y(v) + 4} textAnchor="end">
            {v > 0 ? "+" : ""}
            {v.toFixed(1)}
          </text>
        </g>
      ))}
      {sides.map((side, i) => (
        <g key={side} data-series={i}>
          <polyline
            points={rows
              .map((r, n) => `${x(n)},${y(r[side] / r.holes)}`)
              .join(" ")}
            fill="none"
          />
          {rows.map((r, n) =>
            i === 0 ? (
              <circle
                key={r.roundId}
                cx={x(n)}
                cy={y(r[side] / r.holes)}
                r="3.5"
              />
            ) : (
              <rect
                key={r.roundId}
                x={x(n) - 3}
                y={y(r[side] / r.holes) - 3}
                width="6"
                height="6"
              />
            ),
          )}
          <line
            className="rc-form-connector"
            x1="254"
            y1={ends[i]}
            x2="265"
            y2={labelY(i)}
          />
          <text x="270" y={labelY(i) + 4}>
            {signed(last[side] / last.holes)}
          </text>
        </g>
      ))}
      <text className="rc-form-axis" x="38" y="164">
        Older rounds
      </text>
      <text className="rc-form-axis" x="254" y="164" textAnchor="end">
        Latest
      </text>
    </svg>
  );
}

function trendCoordinates(
  rows: TrendRows,
  sides: ["left" | "right", "left" | "right"],
) {
  const values = rows.flatMap((r) => [r.left / r.holes, r.right / r.holes]);
  const low = Math.floor(Math.min(...values) * 2) / 2,
    high = Math.max(low + 0.5, Math.ceil(Math.max(...values) * 2) / 2);
  const x = (i: number) => 38 + (i / (rows.length - 1)) * 216;
  const y = (v: number) => 22 + ((high - v) / (high - low)) * 120;
  const last = rows.at(-1)!;
  const ends = sides.map((s) => y(last[s] / last.holes));
  const close = Math.abs(ends[0]! - ends[1]!) < 22;
  const labelY = (i: number) =>
    close
      ? (ends[0]! + ends[1]!) / 2 +
        (ends[i]! <= ends[1 - i]! ? -12 : 12) +
        (ends[0] === ends[1] && i === 1 ? 24 : 0)
      : ends[i]!;
  return { low, high, x, y, last, ends, labelY };
}
